import * as XLSX from 'xlsx'

import dayjs from 'dayjs'

import { Tooltip } from 'antd'
import { AxiosError } from 'axios'
import { saveAs } from 'file-saver'
import { TFunction } from 'i18next'
import { createElement } from 'react'
import { MenuItem } from 'src/stores/menu-store'
import { AVATAR_BG_COLORS, TIME_ZONE_MAP } from 'src/constants'
import { GroupedWarning, SalesOrderWarning } from 'src/types/global'
import { menuIcons, menuPaths } from 'src/components/organisms/sidebar/data'
import { ModalData } from 'src/components/organisms/commercial/modals/mds-moq/types'

type FormattedMenuItem = {
  key: string
  label: string | React.ReactNode
  icon?: React.ReactNode
  children?: FormattedMenuItem[]
  isSelected?: (pathname: string) => boolean
}

type MenuIcons = {
  [key: string]: React.ComponentType
}

type MenuPaths = {
  [key: string]: string
}

const typedMenuIcons: MenuIcons = menuIcons
const typedMenuPaths: MenuPaths = menuPaths

export type MdsRawItem = {
  ztypeMoq?: string
  ztypeMds?: string
  posnr?: string
  matnr?: string
  maktx?: string
  zmoq?: string
  ctqty?: string
  zuom?: string
  zqtaTr?: string
  Zqta?: string
  zieme?: string
  zsnote?: string
  stprs?: string
  waers?: string
}

const removeQueryParams = (path: string) => {
  if (!path) return ''

  if (path.includes('?')) {
    return path.split('?')[0]
  }
  return path
}

export const formatMenuItems = (
  items: MenuItem[],
  t: TFunction
): FormattedMenuItem[] => {
  const getRoutePath = (item: MenuItem) => {
    return item.CrudName
      ? `/common/${item.CrudName}`
      : typedMenuPaths[item.Description]
        ? typedMenuPaths[item.Description]
        : item.Description
  }

  const shouldShowTooltip = (text: string) => text.length > 20

  return items
    .filter((el) => el.IsActive)
    .map((el) => {
      return {
        key: getRoutePath(el),
        label: t(`menu.${el.Description}`),
        ...(typedMenuIcons[el.Description]
          ? { icon: createElement(typedMenuIcons[el.Description]) }
          : {}),
        ...(el.Children?.length
          ? {
              children: el.Children.map((c) => ({
                key: getRoutePath(c),
                label: t(`menu.${c.Description}`),
                ...(c.Children.length ? { type: 'group' } : {}),
                ...(c.Children.length
                  ? {
                      children: c.Children.map((item) => ({
                        key: getRoutePath(item),
                        label: shouldShowTooltip(t(`menu.${item.Description}`))
                          ? createElement(
                              Tooltip,
                              {
                                title: t(`menu.${item.Description}`),
                                placement: 'right',
                              },

                              t(`menu.${item.Description}`)
                            )
                          : t(`menu.${item.Description}`),
                      })),
                    }
                  : {}),
              })),
            }
          : {}),
        isSelected: (path: string) => path === getRoutePath(el),
      }
    })
}

export const getCountryName = (code: string, locale = 'en') => {
  if (!code) {
    return ''
  }
  const regionNames = new Intl.DisplayNames([locale], { type: 'region' })
  return regionNames.of(code.toUpperCase())
}

type AnyObject = { [key: string]: any } //eslint-disable-line

export function removeEmptyKeys(obj: AnyObject) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== '' //eslint-disable-line
    )
  )
}

export function transformObject(obj: AnyObject): AnyObject {
  const transformedObj: AnyObject = {}

  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      transformedObj[key] = obj[key].join(',')
    } else {
      transformedObj[key] = obj[key]
    }
  }

  return transformedObj
}

const stringToBoolean = (str: string) => {
  if (str === 'true') {
    return true
  }
  if (str === 'false') {
    return false
  }
  return str
}

export function reverseTransformObject(
  obj: AnyObject,
  arrayFields?: string[],
  dateFilters?: string[]
): AnyObject {
  const transformedObj: AnyObject = {}

  for (const key in obj) {
    if (
      typeof obj[key] === 'string' &&
      (obj[key].includes(',') || arrayFields?.includes(key))
    ) {
      transformedObj[key] = obj[key]
        .split(',')
        .map((item: string) => item.trim())
    } else if (dateFilters?.includes(key)) {
      transformedObj[key] = dayjs(obj[key])
    } else {
      transformedObj[key] = stringToBoolean(obj[key])
    }
  }

  return transformedObj
}

export const selectSearchFilter = (
  input: string,
  option: AnyObject | undefined
) => {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
}

export const groupBy = (
  array: AnyObject[],
  key: string
): Record<string, AnyObject[]> => {
  return array.reduce(
    (acc, item) => {
      const value = item[key]
      ;(acc[value] = acc[value] || []).push(item)
      return acc
    },
    {} as Record<string, AnyObject[]>
  )
}

export const getSelectedMenuKeys = (
  items: FormattedMenuItem[],
  pathname: string
) => {
  const selectedKeys: string[] = []

  items.map((item) => {
    if (item.isSelected) {
      if (item.isSelected(pathname)) {
        selectedKeys.push(item.key)
      }
    } else {
      if (removeQueryParams(item.key) === pathname) {
        selectedKeys.push(item.key)
      }
    }
    if (item.children) {
      const itemKeys = getSelectedMenuKeys(item.children, pathname)
      selectedKeys.push(...itemKeys)
    }
  })

  return selectedKeys
}

export const removeIsSelectedFromMenuItems = (
  items: FormattedMenuItem[]
): Omit<FormattedMenuItem, 'isSelected'>[] => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return items.map(({ isSelected, children, ...item }) => {
    const cleanedItem: Omit<FormattedMenuItem, 'isSelected'> = { ...item }

    if (children) {
      cleanedItem.children = removeIsSelectedFromMenuItems(children)
    }

    return cleanedItem
  })
}

export function getRandomAvatarBgColor() {
  const randomIndex = Math.floor(Math.random() * AVATAR_BG_COLORS.length)
  return AVATAR_BG_COLORS[randomIndex]
}

export const groupByArray = <T, K extends keyof T>(
  array: T[],
  key: K
): Record<string | number | symbol, T[]> => {
  return array?.reduce(
    (result, item) => {
      const groupKey = item[key] as string | number | symbol
      ;(result[groupKey] = result[groupKey] || []).push(item)
      return result
    },
    {} as Record<string | number | symbol, T[]>
  )
}

export const groupTable = <T, K extends keyof T>(
  array: T[],
  key: K
): Map<string, T[]> => {
  return array.reduce((result, item) => {
    const groupKey = String(item[key])
    if (!result.has(groupKey)) {
      result.set(groupKey, [])
    }
    result.get(groupKey)!.push(item)
    return result
  }, new Map<string, T[]>())
}

type ParseFilterOptions = {
  arrayFields?: string[]
  dateFields?: string[]
  numberFields?: string[]
}

export const parseFiltersFromUrl = (
  params: URLSearchParams,
  options?: ParseFilterOptions
) => {
  const filters: Record<string, string | number | Array<string | number>> = {}

  params?.forEach((value, key) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsedValue: any = value
    // Check for boolean values
    if (value === 'true') {
      parsedValue = true
    } else if (value === 'false') {
      parsedValue = false
    }

    // Check for numeric values
    if (options?.numberFields?.includes(key)) {
      parsedValue = Number(value)
    }

    // Check for date values
    if (options?.dateFields?.includes(key)) {
      parsedValue = dayjs(value)
    }

    // Handling array values
    if (filters[key]) {
      if (Array.isArray(filters[key])) {
        // eslint-disable-next-line no-extra-semi
        ;(filters[key] as (string | number)[]).push(parsedValue)
      } else {
        filters[key] = [filters[key], parsedValue]
      }
    } else {
      if (options?.arrayFields?.includes(key)) {
        filters[key] = [parsedValue]
      } else {
        filters[key] = parsedValue
      }
    }
  })

  return filters
}

export function getNameInitials(fullName: string): string {
  const names = fullName.trim().split(' ')

  if (names.length === 1) {
    return names[0][0].toUpperCase()
  }

  const firstNameInitial = names[0][0].toUpperCase()
  const lastNameInitial = names[names.length - 1][0].toUpperCase()

  return `${firstNameInitial}${lastNameInitial}`
}

export function isISODate(value: string): boolean {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/
  return isoDateRegex.test(value)
}

export function pascalToCamel(str: string): string {
  if (!str) return ''
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export function exportExcelFile(
  worksheet: XLSX.WorkSheet,
  title?: string
): void {
  const workbook = XLSX.utils.book_new()
  const timestamp = new Date().getTime()
  const fileName = title ? `${title}_${timestamp}.xlsx` : `${timestamp}.xlsx`

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    title ?? timestamp.toString()
  )
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  })

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  saveAs(blob, fileName)
}

export const getWindowsTimeZone = (): string => {
  const ianaTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return TIME_ZONE_MAP[ianaTimeZone] || 'Unknown Time Zone'
}

export const isValidString = (text: unknown): text is string => {
  return typeof text === 'string' && text.trim() !== ''
}

export const formatElapsedTime = (elapsedTime: string): string => {
  const [dayPart, timePart] = elapsedTime.includes('.')
    ? elapsedTime.split('.')
    : ['0', elapsedTime]
  const [hoursStr, minutesStr, secondsStr] = timePart.split(':')

  const days = Number(dayPart)
  const hours = Number(hoursStr)
  const minutes = Number(minutesStr)
  const seconds = Number(secondsStr)

  const parts: string[] = []

  if (days) parts.push(`${days}d`)
  if (hours) parts.push(`${hours}h`)
  if (minutes) parts.push(`${minutes}m`)
  if (seconds) parts.push(`${seconds}seg`)

  return parts.join(' ')
}

export function getErrorMessage(err: AxiosError) {
  const message =
    typeof err.response?.data === 'object' &&
    err.response?.data !== null &&
    'message' in err.response.data
      ? (err.response.data as { message: string }).message
      : undefined

  return message
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildQueryParams(params: Record<string, any>) {
  const entries: string[] = []

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return

    if (Array.isArray(value)) {
      value.forEach((v) => {
        entries.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`
        )
      })
    } else {
      entries.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
    }
  })

  return entries.join('&')
}

export const toNumberOrNull = (
  value: string | number | null | undefined
): number | null => {
  if (value === '' || value === null || value === undefined) return null
  const num = Number(value)
  return isNaN(num) ? null : num
}

export const toBooleanOrNull = (
  value: string | number | boolean | null | undefined
): boolean | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim()
    if (lower === 'true' || lower === '1') return true
    if (lower === 'false' || lower === '0') return false
    return null
  }
  if (typeof value === 'number') {
    return value === 1 || value === 1.0
  }
  return null
}

export const getTableValues = ({
  code,
  description,
}: {
  code: string
  description: string
}) => {
  return code || description
    ? `${code ? code : ''}${code && description ? ' - ' : ''}${description ? description : ''}`
    : '-'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformMdsData = (data: any): ModalData[] => {
  return data.map((item: MdsRawItem) => ({
    moq: item.ztypeMoq || '',
    mds: item.ztypeMds || '',
    item: item.posnr || '',
    material: item.matnr || '',
    materialDescription: item.maktx || '',
    maxLimit: parseFloat(item.zmoq || '0'),
    quantity: parseInt(item.ctqty || '0', 10),
    uom: item.zuom || '',
    threshold2: parseFloat(item.zqtaTr || '0'),
    deltaQuantity: parseFloat(item.Zqta || '0'),
    uom2: item.zieme || '',
    docNotes: item.zsnote || '',
    surcharge: parseFloat(item.stprs || '0'),
    currency: item.waers || '',
  }))
}

export const groupWarningsByMessage = (
  warnings: SalesOrderWarning[]
): GroupedWarning[] => {
  const groupedMap = new Map<string, GroupedWarning>()

  warnings.forEach(
    ({ materialId, materialCode, materialDescription, messages }) => {
      messages.forEach((message) => {
        if (!groupedMap.has(message)) {
          groupedMap.set(message, {
            message,
            materials: [],
          })
        }

        groupedMap.get(message)!.materials.push({
          materialId,
          materialCode,
          materialDescription,
        })
      })
    }
  )

  return Array.from(groupedMap.values())
}

export function filterBySearch<T>(
  data: T[] | undefined,
  search: string,
  fields: (keyof T)[]
): T[] {
  if (!data || !search) return data ?? []

  const searchValue = search.toLowerCase()

  return data.filter((item) =>
    fields.some((field) => {
      const value = item[field]
      return value?.toString().toLowerCase().includes(searchValue)
    })
  )
}
